import React from 'react';
import { Link } from 'react-router-dom';
import { getPhaseLabel } from '../../utils/tournamentPhases';

/**
 * TournamentCard - Displays a tournament summary for the player portal.
 * @param {Object} playerRecord - Player document with populated tournament
 * @param {boolean} compact - If true, render a smaller card for past tournaments
 */
const TournamentCard = ({ playerRecord, compact = false }) => {
  const tournament = playerRecord.tournament || playerRecord.tournamentId || {};
  const tournamentId = tournament._id || playerRecord.tournamentId;
  const portalStatus = tournament.portalStatus || 'signup';
  const tournamentDate = tournament.date ? new Date(tournament.date) : null;

  const statusColorMap = {
    'signup': 'bg-blue-100 text-blue-700',
    'roster-locked': 'bg-yellow-100 text-yellow-700',
    'tee-sheet-published': 'bg-green-100 text-green-700',
    'gameday': 'bg-orange-100 text-orange-700',
    'completed': 'bg-gray-100 text-gray-700',
    'recap-posted': 'bg-purple-100 text-purple-700',
  };

  const badgeClass = statusColorMap[portalStatus] || 'bg-gray-100 text-gray-700';

  if (compact) {
    return (
      <Link
        to={`/portal/tournaments/${tournamentId}`}
        className="block bg-white rounded-lg p-3 border border-gray-100 hover:border-green-300 hover:shadow-sm transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 text-sm truncate">
              {tournament.name || 'Tournament'}
            </p>
            <p className="text-xs text-gray-500">
              {tournamentDate ? tournamentDate.toLocaleDateString() : ''}
            </p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ml-2 ${badgeClass}`}>
            {getPhaseLabel(portalStatus)}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/portal/tournaments/${tournamentId}`}
      className="block bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-green-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-semibold text-gray-900">
          {tournament.name || 'Tournament'}
        </h4>
        <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ml-2 ${badgeClass}`}>
          {getPhaseLabel(portalStatus)}
        </span>
      </div>

      <p className="text-sm text-gray-500 mb-2">
        {tournamentDate
          ? tournamentDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : 'Date TBD'}
      </p>

      {/* Show tee time if player is assigned */}
      {playerRecord.assignedTeeTime && (
        <p className="text-sm text-green-600 font-medium">
          Tee time: {playerRecord.assignedTeeTime}
        </p>
      )}

      {/* Action hint based on phase */}
      <p className="text-xs text-gray-400 mt-2">
        {portalStatus === 'roster-locked' && 'Submit your preferences'}
        {portalStatus === 'tee-sheet-published' && 'View your tee time'}
        {portalStatus === 'gameday' && 'Good luck today!'}
        {portalStatus === 'completed' && 'Share your feedback'}
        {portalStatus === 'recap-posted' && 'View recap and results'}
      </p>

      <div className="flex items-center justify-end mt-2 text-gray-400">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
};

export default TournamentCard;
